import { api } from '@udm/contracts';
import { create } from 'zustand';
import { ApiRequestError, request } from '../../api/client';

/**
 * Device commands: POST creates the command (202, usually DISPATCHED or FAILED/DEVICE_OFFLINE); its lifecycle then
 * arrives as realtime `cmd` events and updates this store — no polling, no refetch.
 */
export interface CommandState {
  id: string;
  deviceId: string;
  capability: string;
  params: Record<string, unknown>;
  status: api.CommandStatus;
  errorCode: string | null;
  /** params of a refusal (e.g. the rate limit: max_actions, window_minutes, retry_after_s) */
  errorParams?: Record<string, unknown>;
}

interface CommandsStore {
  byId: Record<string, CommandState>;
  /** latest command per device (drives the "sending…/done/failed" line of the controls) */
  lastByDevice: Record<string, string>;
}

export const useCommands = create<CommandsStore>(() => ({ byId: {}, lastByDevice: {} }));

const TERMINAL = new Set<string>(api.TERMINAL_COMMAND_STATUSES);
export const isTerminal = (s: api.CommandStatus) => TERMINAL.has(s);

function upsert(c: CommandState, makeLatest: boolean) {
  useCommands.setState((s) => {
    const prev = s.byId[c.id];
    // Never move a finished command back (a late realtime event may arrive after the POST response).
    if (prev && isTerminal(prev.status) && !isTerminal(c.status)) return s;
    return {
      byId: { ...s.byId, [c.id]: { ...prev, ...c } },
      lastByDevice: makeLatest ? { ...s.lastByDevice, [c.deviceId]: c.id } : s.lastByDevice,
    };
  });
}

/** Realtime `cmd` event (only commands of devices the user may see arrive here). */
export function onCommandEvent(id: string, p: Record<string, unknown>): void {
  const parsed = api.CommandRecord.safeParse(p);
  if (!parsed.success) return;
  const r = parsed.data;
  const known = useCommands.getState().byId[id] !== undefined;
  upsert(
    {
      id,
      deviceId: r.device_id,
      capability: r.capability,
      params: r.params,
      status: r.status,
      errorCode: r.error_code,
    },
    // Commands sent by other users also show up as the device's latest action.
    !known,
  );
}

/** crypto.randomUUID exists only in secure contexts; plain-HTTP dev access needs a fallback. */
const idempotencyKey = () =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;

export async function sendCommand(
  deviceId: string,
  capability: string,
  params: Record<string, unknown> = {},
): Promise<CommandState> {
  try {
    const r = await request(
      'POST',
      `/api/v1/devices/${deviceId}/commands`,
      { capability, params, idempotency_key: idempotencyKey() },
      (v) => api.CommandRecord.parse(v),
    );
    const c: CommandState = {
      id: r.id,
      deviceId,
      capability,
      params,
      status: r.status,
      errorCode: r.error_code,
    };
    upsert(c, true);
    return useCommands.getState().byId[r.id]!;
  } catch (e) {
    const c: CommandState = {
      id: `local-${Date.now()}`,
      deviceId,
      capability,
      params,
      status: 'FAILED',
      errorCode: e instanceof ApiRequestError ? e.code : 'NETWORK_ERROR',
      ...(e instanceof ApiRequestError && e.params ? { errorParams: e.params } : {}),
    };
    upsert(c, true);
    return c;
  }
}

/** Clears command state at sign-out. */
export function clearCommands(): void {
  useCommands.setState({ byId: {}, lastByDevice: {} });
}

/** Resolves when the command reaches a terminal status (realtime `cmd` events), or after `ms`. */
export function settled(c: CommandState, ms = 60_000): Promise<CommandState> {
  if (isTerminal(c.status)) return Promise.resolve(c);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsub();
      resolve(useCommands.getState().byId[c.id] ?? c);
    }, ms);
    const unsub = useCommands.subscribe(() => done());
    function done() {
      const cur = useCommands.getState().byId[c.id];
      if (cur && isTerminal(cur.status)) {
        unsub();
        clearTimeout(timer);
        resolve(cur);
      }
    }
    done();
  });
}
